import axios from "axios";
import { TFunction } from "i18next";

/**
 * A message the therapist can act on for a failed AI analysis request.
 *
 * Analysis refuses for reasons the therapist can fix — notes still too short,
 * consent not signed, the tool not adopted — so every answer is turned into
 * something on screen. Saying nothing reads as the button being broken.
 *
 * The API reports the reason as an English `detail`, which is matched here
 * only for the two 400s the analysis service raises; anything else falls back
 * to the status.
 */
export function analyzeErrorMessage(error: unknown, t: TFunction): string {
  if (!axios.isAxiosError(error) || !error.response) {
    return t("session.analyzeError.generic", "Could not analyze the session. Please try again.");
  }

  const { status, data } = error.response;
  const detail = typeof data?.detail === "string" ? data.detail : "";

  if (status === 409 && detail === "ai_consent_required") {
    return t("patient.portalConsent.aiBlocked");
  }

  if (status === 403) {
    return detail === "ai_insights_tool_not_adopted"
      ? t("session.analyzeError.toolNotAdopted", "Adopt the AI Insights tool to analyze sessions.")
      : t("session.analyzeError.forbidden", "You do not have access to this session.");
  }

  if (status === 404) {
    return t("session.analyzeError.notFound", "This session no longer exists.");
  }

  if (status === 400) {
    if (detail.includes("too short")) {
      return t(
        "session.analyzeError.notesTooShort",
        "The notes are too short to analyze. Add more detail and try again.",
      );
    }
    if (detail.includes("empty")) {
      return t("session.analyzeError.notesEmpty", "Write the session notes before analyzing them.");
    }
    return t("session.analyzeError.invalidNotes", "These notes cannot be analyzed yet.");
  }

  return t("session.analyzeError.generic", "Could not analyze the session. Please try again.");
}
